import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowLeft as ArrowLeftIcon,
  KeyboardArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import { Box, IconButton, ListSubheader, MenuItem, Select, useMediaQuery } from '@mui/material';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { COLORS } from '@/components/dashboard/scoring/constants';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { DocumentClassificationType } from '@/models/DocumentRecord';
import { generateDocumentRecordUrl } from '@/services/api/document-records/api';
import {
  buildSellerDisclosureViewerForms,
  getSellerDisclosureAcronym,
  withPdfPage,
} from '@/utils/sellerDisclosureDocuments';

const font = "'Manrope', sans-serif";
const PdfDocumentViewer = lazy(() =>
  import('@/components/common/PdfDocumentViewer').then(({ PdfDocumentViewer: Viewer }) => ({
    default: Viewer,
  })),
);

// The bookmark list is Hauser's own construct — agents compile the physical
// packet in whatever order they like, but the list of jump-links to it is
// ours to organize. Grouped by what these forms actually are, not just a
// flat priority cut: the seller/agent's own account of the property, risk
// exposure, HOA (which already has its own dedicated tab elsewhere), and
// everything else administrative/advisory.
const BOOKMARK_GROUPS: Array<{ title: string; formKeys: string[] }> = [
  { title: 'Seller & Agent Disclosures', formKeys: ['TDS', 'SPQ', 'AVID'] },
  {
    title: 'Hazards & Risk',
    formKeys: [
      'NHD',
      'ENVIRONMENTAL_HAZARDS_ACKNOWLEDGMENT',
      'LPD',
      'FHDS',
      'DSDT',
      'WDA',
      'BHIA',
      'LOCAL_THIRD_PARTY_HAZARD_DISCLOSURES',
    ],
  },
  { title: 'HOA / Shared Property', formKeys: ['BHAA'] },
];
const OTHER_DOCUMENT_FORM_KEYS = new Set([
  'PTR',
  'AFFILIATED_BUSINESS_DISCLOSURE',
  'UNKNOWN_SELLER_DISCLOSURE',
]);
const OTHER_DOCUMENTS_GROUP_TITLE = 'Other Documents';
const ADVISORIES_GROUP_TITLE = 'Advisories & Administrative';

/**
 * Shows the actual compiled seller-disclosure PDF rather than a
 * Hauser-rendered recreation of it. Every bug found while building the
 * recreated version (wrong order, duplicate explanations, truncated labels,
 * wrong section grouping) came from the same root cause: trying to make the
 * UI *be* the document instead of just showing it. The document is always
 * exactly right, by definition. A future AI insight panel can point out
 * things worth a second look — it should not replace seeing the source.
 */
export const SellerDisclosuresView = () => {
  // Match DashboardLayout exactly. The theme's `down('md')` query stops just
  // below 768px, which left the common 768px DevTools viewport with a mobile
  // shell but the desktop bookmark rail.
  const isMobile = useMediaQuery('(max-width: 768px)');
  // a narrow computer (or a wide one with panels open) keeps the document full width and moves the outline into the
  // compact picker above it
  const isCompact = useMediaQuery('(max-width: 1100px)');
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { documentRecords, isFetchingDocumentRecords } = useDocumentRecords(propertyId);

  const sellerDisclosureRecords = useMemo(
    () =>
      (documentRecords ?? []).filter(
        (record) =>
          record.documentType === DocumentClassificationType.SELLER_DISCLOSURE &&
          record.sellerDisclosureManifest,
      ),
    [documentRecords],
  );
  const forms = useMemo(
    () => buildSellerDisclosureViewerForms(sellerDisclosureRecords),
    [sellerDisclosureRecords],
  );

  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const shortcutRailRef = useRef<HTMLDivElement>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const activeForm = forms.find((form) => form.displayKey === activeFormId);
  const activeRecord = activeForm
    ? sellerDisclosureRecords.find((record) => record.id === activeForm.sourceDocumentRecordId)
    : sellerDisclosureRecords[0];

  useEffect(() => {
    let cancelled = false;
    setBaseUrl(null);
    if (!activeRecord) return;
    setIsLoadingUrl(true);
    generateDocumentRecordUrl({ id: activeRecord.id })
      .then((url) => {
        if (!cancelled) setBaseUrl(url ?? null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUrl(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeRecord?.id]);

  useEffect(() => {
    if (forms.length === 0) {
      setActiveFormId(null);
      return;
    }
    if (!forms.some((form) => form.displayKey === activeFormId)) {
      setActiveFormId(forms[0].displayKey);
    }
  }, [forms, activeFormId]);

  const containerSx = {
    pt: 0,
    pb: isMobile ? '10px' : '20px',
    px: isMobile ? '10px' : 0,
    width: '100%',
    boxSizing: 'border-box' as const,
    // Must be a definite height, not minHeight — the card and bookmark
    // list below rely on `flex: 1; min-height: 0` to shrink and scroll
    // internally, which only engages when this ancestor has an actual
    // ceiling to divide up. `minHeight` is a floor: it never stops this
    // box from growing past the viewport to fit content, which defeats
    // flex-shrink entirely no matter how the descendants are set up.
    flex: isMobile ? '0 0 auto' : 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    rowGap: isMobile ? '20px' : 0,
  };
  const centeredStateSx = { ...containerSx, alignItems: 'center', justifyContent: 'center' };

  const isLoading = isFetchingDocumentRecords && !documentRecords;

  if (isLoading) {
    return (
      <Box sx={centeredStateSx}>
        <div style={{ fontSize: 13, color: COLORS.textDim }}>Loading…</div>
      </Box>
    );
  }

  if (sellerDisclosureRecords.length === 0) {
    return (
      <Box sx={centeredStateSx}>
        <div style={{ fontSize: 13, color: COLORS.textDim }}>
          No seller disclosures uploaded yet.
        </div>
      </Box>
    );
  }

  const groupedFormKeys = new Set<string>();
  const bookmarkGroups = BOOKMARK_GROUPS.map((group) => {
    const groupForms = group.formKeys.flatMap((key) =>
      forms.filter((form) => form.formKey === key),
    );
    groupForms.forEach((form) => groupedFormKeys.add(form.displayKey));
    return { title: group.title, forms: groupForms };
  }).filter((group) => group.forms.length > 0);
  const otherDocumentForms = forms.filter(
    (form) => !groupedFormKeys.has(form.displayKey) && OTHER_DOCUMENT_FORM_KEYS.has(form.formKey),
  );
  otherDocumentForms.forEach((form) => groupedFormKeys.add(form.displayKey));
  if (otherDocumentForms.length > 0) {
    bookmarkGroups.push({ title: OTHER_DOCUMENTS_GROUP_TITLE, forms: otherDocumentForms });
  }
  const advisoryForms = forms.filter((form) => !groupedFormKeys.has(form.displayKey));
  if (advisoryForms.length > 0) {
    bookmarkGroups.push({ title: ADVISORIES_GROUP_TITLE, forms: advisoryForms });
  }
  const orderedShortcutForms = bookmarkGroups.flatMap((group) => group.forms);

  const iframeSrc = baseUrl
    ? `${withPdfPage(baseUrl, activeForm?.primaryPage)}&view=FitH`
    : undefined;

  return (
    <Box data-testid="seller-disclosures-view" sx={containerSx}>
      <div
        style={{
          flex: isMobile ? '0 0 auto' : 1,
          minHeight: 0,
          background: COLORS.card,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderRadius: '24px',
          padding: '20px',
          display: 'flex',
          flexDirection: isMobile || isCompact ? 'column' : 'row',
          gap: 20,
        }}>
        {/* Bookmarks-style side list, matching how a real PDF viewer's
            outline panel handles a document with many named sections —
            a flat wrapping row of 19 buttons just gets chaotic, a
            scrollable list doesn't, no matter the count. */}
        {isMobile || isCompact ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: isCompact && !isMobile ? 'row' : 'column',
              alignItems: isCompact && !isMobile ? 'center' : 'stretch',
              gap: isCompact && !isMobile ? '16px' : 0,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: isMobile ? '12px' : 0,
                color: 'var(--tx-text)',
                fontSize: 17,
                fontWeight: 400,
                letterSpacing: '-0.015em',
                flexShrink: 0,
              }}>
              Seller disclosures
            </Box>
            <Select
              aria-label="Seller disclosure document"
              IconComponent={ArrowDownIcon}
              value={activeFormId ?? ''}
              onChange={(event) => setActiveFormId(event.target.value)}
              renderValue={(value) => {
                const form = forms.find((candidate) => candidate.displayKey === value);
                return form ? <DocumentLabel form={form} fontSize={12} /> : 'Select a document';
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: '60dvh',
                    bgcolor: 'var(--tx-dialog)',
                    backgroundImage: 'var(--tx-surface-light)',
                    borderRadius: '16px',
                    boxShadow: 'var(--tx-shadow-sm)',
                  },
                },
              }}
              sx={{
                flexShrink: 0,
                height: 40,
                minWidth: isCompact && !isMobile ? 320 : undefined,
                color: COLORS.text,
                fontFamily: font,
                fontSize: 12,
                fontWeight: 400,
                borderRadius: '12px',
                bgcolor: 'var(--tx-fill)',
                '& .MuiSelect-select': {
                  height: '100%',
                  boxSizing: 'border-box',
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-icon': {
                  right: '12px',
                  fontSize: 18,
                  color: 'var(--tx-faint)',
                  transition: 'transform 0.2s',
                },
              }}>
              {bookmarkGroups.flatMap((group, groupIndex) => [
                <ListSubheader
                  key={`${group.title}-header`}
                  sx={{
                    bgcolor: 'var(--tx-dialog)',
                    mt: groupIndex > 0 ? '10px' : 0,
                    color: 'var(--tx-muted)',
                    fontFamily: font,
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: '34px',
                  }}>
                  {group.title}
                </ListSubheader>,
                ...group.forms.map((form) => (
                  <MenuItem
                    key={form.displayKey}
                    value={form.displayKey}
                    title={form.title}
                    sx={{
                      color: COLORS.textDim,
                      fontFamily: font,
                      fontSize: 12,
                      fontWeight: 400,
                      borderRadius: '8px',
                      border: 'none',
                      borderBottom: 'none',
                      '&::before, &::after': { display: 'none' },
                    }}>
                    <DocumentLabel form={form} reserveChipSpace />
                  </MenuItem>
                )),
              ])}
            </Select>
          </Box>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              minHeight: 0,
              width: 'clamp(220px, 22%, 300px)',
            }}>
            <span
              style={{
                fontFamily: font,
                fontSize: 17,
                fontWeight: 400,
                letterSpacing: '-0.015em',
                color: 'var(--tx-text)',
                padding: '2px 0 16px',
              }}>
              Seller disclosures
            </span>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none' }}>
              {bookmarkGroups.map((group, groupIndex) => (
                <div key={group.title} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span
                    style={{
                      fontFamily: font,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--tx-muted)',
                      padding: groupIndex > 0 ? '20px 0 6px' : '0 0 6px',
                    }}>
                    {group.title}
                  </span>
                  {group.forms.map((form) => (
                    <BookmarkButton
                      key={form.displayKey}
                      form={form}
                      isActive={form.displayKey === activeFormId}
                      onClick={() => setActiveFormId(form.displayKey)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            flex: isMobile ? '0 0 auto' : 1,
            width: '100%',
            minWidth: 0,
            // The page already has a definite remaining-viewport height.
            // Allow this pane to shrink into that space instead of enforcing
            // a 400px floor that can push the card below short mobile screens.
            minHeight: 0,
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'rgba(var(--tx-ink-rgb), 0.05)',
          }}>
          {isLoadingUrl || !baseUrl ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: font,
                fontSize: 13,
                color: COLORS.textDim,
              }}>
              Loading document…
            </div>
          ) : isMobile ? (
            <Suspense fallback={<ViewerLoadingState />}>
              <PdfDocumentViewer url={baseUrl} pageNumber={activeForm?.primaryPage} />
            </Suspense>
          ) : (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title="Seller disclosures"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
          )}
        </div>
      </div>
      {isMobile && (
        <Box
          aria-label="Document shortcuts"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: '100%',
            px: '10px',
            pb: '2px',
            boxSizing: 'border-box',
          }}>
          <IconButton
            aria-label="Scroll document shortcuts left"
            onClick={() => shortcutRailRef.current?.scrollBy({ left: -180, behavior: 'smooth' })}
            sx={{ flexShrink: 0, p: '4px', color: 'var(--hx-ground-muted)' }}>
            <ArrowLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Box
            ref={shortcutRailRef}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}>
            {orderedShortcutForms.map((form) => {
              const isActive = form.displayKey === activeFormId;
              const label = getSellerDisclosureAcronym(form) ?? form.shortLabel;
              return (
                <Box
                  component="button"
                  key={form.displayKey}
                  type="button"
                  title={form.title}
                  onClick={() => setActiveFormId(form.displayKey)}
                  sx={{
                    flexShrink: 0,
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    border: 'none',
                    borderRadius: '9px',
                    // the rail sits on the chalk ground, so each chip is a slate tile (the card's surface) to stay legible
                    bgcolor: isActive ? 'var(--tx-glass-card)' : 'var(--tx-glass-panel)',
                    boxShadow: 'var(--tx-shadow-sm)',
                    color: isActive ? 'var(--tx-text)' : 'var(--tx-muted)',
                    px: '10px',
                    py: '6px',
                    fontFamily: font,
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}>
                  {label}
                </Box>
              );
            })}
          </Box>
          <IconButton
            aria-label="Scroll document shortcuts right"
            onClick={() => shortcutRailRef.current?.scrollBy({ left: 180, behavior: 'smooth' })}
            sx={{ flexShrink: 0, p: '4px', color: 'var(--hx-ground-muted)' }}>
            <ArrowRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

function ViewerLoadingState() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: font,
        fontSize: 13,
        color: COLORS.textDim,
      }}>
      Loading document…
    </div>
  );
}

function DocumentLabel({
  form,
  fontSize,
  reserveChipSpace = false,
}: {
  form: { formKey: string; shortLabel: string; title: string };
  fontSize?: number;
  reserveChipSpace?: boolean;
}) {
  const acronym = getSellerDisclosureAcronym(form);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', minWidth: 0, fontSize }}>
      {acronym && (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            width: 60,
            boxSizing: 'border-box',
            textAlign: 'center',
            color: 'var(--tx-text)',
            bgcolor: 'var(--tx-fill-2)',
            borderRadius: '6px',
            px: '6px',
            py: '3px',
            fontSize: 10,
            fontWeight: 600,
          }}>
          {acronym}
        </Box>
      )}
      {!acronym && reserveChipSpace && <Box component="span" sx={{ width: 60, flexShrink: 0 }} />}
      <Box
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {acronym ? form.title : form.shortLabel}
      </Box>
    </Box>
  );
}

function BookmarkButton({
  form,
  isActive,
  onClick,
}: {
  form: { formKey: string; shortLabel: string; title: string };
  isActive: boolean;
  onClick: () => void;
}) {
  const acronym = getSellerDisclosureAcronym(form);
  return (
    <button
      onClick={onClick}
      title={form.title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: font,
        fontSize: 13,
        fontWeight: isActive ? 600 : 500,
        color: isActive ? 'var(--tx-text)' : 'var(--tx-muted)',
        background: isActive ? 'var(--tx-fill-2)' : 'transparent',
        border: 'none',
        borderRadius: 12,
        padding: '8px 10px',
        margin: '0 -10px',
        cursor: 'pointer',
        textAlign: 'left',
      }}>
      {acronym && (
        <span
          style={{
            flexShrink: 0,
            width: 52,
            boxSizing: 'border-box',
            textAlign: 'center',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--tx-text)',
            background: isActive ? 'var(--tx-fill-3)' : 'var(--tx-fill-2)',
            borderRadius: 6,
            padding: '3px 6px',
          }}>
          {acronym}
        </span>
      )}
      {!acronym && <span aria-hidden="true" style={{ width: 52, flexShrink: 0 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {acronym ? form.title : form.shortLabel}
      </span>
    </button>
  );
}
